/*************************
 * @license
 * Copyright 2024 Myenergi Ltd. All rights reserved.
 * No part of this work may be reproduced, stored in a retrieval system of any nature, or transmitted, in any form or by any means without the prior written permission of Myenergi Ltd., the copyright owner.
 * If any unauthorised acts are carried out in relation to this copyright work, a civil claim for damages may be made and/or a criminal prosecution may result.
 *************************/
import { action, computed, makeObservable, observable } from "mobx";
import { persist } from "mobx-persist";
import { StoreBase } from "./StoreBase";

export enum AlertType {
  error = "error",
  warning = "warning",
  success = "success",
}

export interface Notification {
  messageText: string;
  type: AlertType;
  isOpened: boolean;
  autoHide?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyType = any;

export class NotificationStore extends StoreBase {
  @observable @persist("object") notification: Notification = {
    messageText: "Placeholder :3",
    type: AlertType.success,
    isOpened: false,
    autoHide: true,
  };

  constructor() {
    super();
    makeObservable(this);
  }

  @action.bound
  public triggerNotificationForPromise<T = AnyType>(
    awaitable: Promise<T>,
    successText?: string,
    failureText?: string,
  ): Promise<T> {
    awaitable
      .then(() => {
        this.setNotification({
          messageText: successText || "Operation succeeded",
          type: AlertType.success,
          isOpened: true,
          autoHide: true,
        });
      })
      .catch((e) => {
        this.setNotification({
          messageText:
            failureText || e.message || "Operation ran into an error",
          type: AlertType.error,
          isOpened: true,
          autoHide: true,
        });
      });

    return awaitable;
  }

  @action.bound
  public triggerNotification(
    messageText?: string,
    type?: AlertType,
    autoHide = true,
  ) {
    this.setNotification({
      messageText: messageText || "Operation succeeded",
      type: type || AlertType.success,
      isOpened: true,
      autoHide,
    });
  }

  @action.bound
  public closeNotification() {
    this.notification.isOpened = false;
  }

  @computed get currentNotification(): Notification {
    return this.notification;
  }

  @action.bound
  private setNotification(notification: Notification) {
    this.notification = notification;
  }
}

export default NotificationStore;
